// react
import React, { useRef, useState, useEffect, useContext, useMemo, useCallback, useLayoutEffect } from "react";

// component library
import DataTable, { TD_TYPE, ColumnTemplate } from "@acadia-ui/datatable";
import { DataProvider, helpers } from "@acadia-ui/theme";
import AppSDK from "@growthos/ui-app-sdk";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import Popover from "@acadia-ui/popover";
import Checkbox from "@acadia-ui/checkbox";
import Chip, { CHIP_TYPE } from "@acadia-ui/chip";
// custom components
import { UnarchiveAction } from "../actions";
import { ARCHIVE_REFRESH_REQUESTED_ORIGINATOR, ViewMasterCampaignsContext, rowOptions } from "../index";
import { PlatformStatusIcon } from "../../../Components/Partials/Icons/PlatformStatusIcon";

// utils
import { doSearch, doFilter, TaxonomyCellRenderer, CustomDataProvider, hideAccordionControlRenderer } from "../utils";
import { FiltersSidesheet } from "../FiltersSidesheet";
import Textbox, { TEXTBOX_SIZE } from "@acadia-ui/textbox";
import { DataTableContext } from "./Overview";
import Heading, { HEADING_LEVEL, HEADING_SIZE } from "@acadia-ui/heading";
import EmptyState from "@acadia-ui/empty-state";

const customDateRenderer = (value) => {
	// prettier-ignore
	return (
		<>
			{value &&
				new Date(value)?.toLocaleDateString?.(
					"en-US",
					{ year: "numeric", month: "short", day: "2-digit" }
				)}
		</>
	);
};

export default () => {
	// Context from index (api calls mostly inform these values)
	const mvcc = useContext(ViewMasterCampaignsContext);

	const {
		// this comment exists to prevent eslint from making this unreadable.
		// data,
		// loading,
		platforms,
		enabledPlatformAPIs,
		// dataKeyDataValueFilters,
		refreshData: refreshDataRaw,
		selectedTab,
		archiveRefreshRequested,
		setArchiveRefreshRequested,
		isUSCurrency
	} = mvcc;

	const dataTableApi = useRef();
	const [dataProvider, setDataProvider] = useState();
	const [loading, setLoading] = useState(false);

	useLayoutEffect(() => {
		setTimeout(() => {
			if (!loading) {
				dataProvider?.resetExpandedAccordionsVisually?.();
			}
		}, 1);
	}, [dataProvider, loading]);

	useEffect(() => {
		if (selectedTab === 1) {
			if (!dataProvider) {
				const dp = new CustomDataProvider({
					id: "archive-table",
					data: [],
					totalRows: 0,
					currentPage: 0,
					childDataProviders: [],
					archive: true,
					requestData: async (...args) => {
						dataTableApi?.current?.element?.current?.querySelector?.(".gos-dt-table")?.scrollTo?.({ top: 0, behavior: "instant" });
						setLoading(true);
						const response = await refreshDataRaw(...args);
						setLoading(false);
						return response;
					}
				});

				setDataProvider(dp);
			} else if (archiveRefreshRequested === ARCHIVE_REFRESH_REQUESTED_ORIGINATOR.OVERVIEW_TAB) {
				dataProvider.update();
				setArchiveRefreshRequested(ARCHIVE_REFRESH_REQUESTED_ORIGINATOR.NONE);
			}
		}
	}, [dataProvider, selectedTab, archiveRefreshRequested]);

	// filter the platforms based on which ones are actually available in the ArchivedCampaigns data set
	const availablePlatforms = useMemo(() => {
		return platforms;
		// TODO: ask if we can nix this filtering logic since we're paginating now. else, we will need to handle it via api call
		// if (platforms && preparedData) {
		// 	return platforms?.filter?.((platform) => {
		// 		return preparedData?.some?.(({ ArchivedCampaigns }) => {
		// 			return Object.values(ArchivedCampaigns)?.some((platformCampaigns) => {
		// 				return platformCampaigns?.some(({ MasterPlatformID }) => MasterPlatformID == platform.MasterPlatformID);
		// 			});
		// 		});
		// 	});
		// }
		// return null;
	}, [platforms /* , preparedData */]);

	const enabledPlatformIds = useMemo(() => {
		return enabledPlatformAPIs?.filter(({ SyncStatus }) => SyncStatus == true)?.map(({ MasterPlatformID }) => MasterPlatformID?.toString()) || [];
	}, [enabledPlatformAPIs]);

	const quickFilters = useMemo(() => {
		if (availablePlatforms) {
			const platformOptions = availablePlatforms.map(({ label, MasterPlatformID }) => (
				<Checkbox id={`archive-platform-${MasterPlatformID}`} value={MasterPlatformID}>
					{label}
				</Checkbox>
			));

			return [
				<Chip type={CHIP_TYPE.LIST} id="archive-filter-platform-name" listItems={platformOptions} isClearable>
					Activation Platform
				</Chip>
			];
		}
		return null;
	}, [availablePlatforms]);

	const columns = [
		<ColumnTemplate
			key="MasterCampaignUniqueIDCode"
			bind="MasterCampaignUniqueIDCode"
			type={TD_TYPE.ACCORDION_CONTROL}
			minWidth={true}
			onRender={(_id, row) => hideAccordionControlRenderer(dataProvider, row)}
			hasRenderCache={false}
		></ColumnTemplate>,

		<ColumnTemplate
			isSortable
			type={TD_TYPE?.TEXT}
			key={"CampaignName_Friendly"}
			bind={"CampaignName_Friendly"}
			onRender={(value, rowData) => {
				const showRestore =
					// master campaign is archived
					rowData?.IsArchived === true ||
					// ...or activation campaign is archived, and it's master campaign is not archived.
					(rowData?.IsArchived === "True" && !dataProvider?.data?.find(({ MasterCampaignID }) => MasterCampaignID == rowData?.ParentUniqueIDcode)?.IsArchived);
				return (
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						{showRestore && (
							<Popover
								trigger={
									<Button
										display={BUTTON_DISPLAY.ICON}
										size={BUTTON_SIZE.SMALL}
										icon="icon-more"
										data-tracking-id-alt="open-unarchive-campaign-modal-popover-trigger"
									>
										Restore
									</Button>
								}
							>
								<UnarchiveAction rowData={rowData} type={rowData?.ParentUniqueIDcode ? "activation" : "master"} />
							</Popover>
						)}
						{rowData?.ParentUniqueIDcode && <TaxonomyCellRenderer taxonomy={rowData?.CampaignName} />}
						{value}
					</div>
				);
			}}
			columnSettings={{ label: "Campaign Name", canHide: false, defaultHidden: false }}
		>
			Campaign name
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			type={TD_TYPE?.TEXT}
			isSortable
			key={"PlatformName"}
			bind={"PlatformName"}
			columnSettings={{ label: "Activation Platform", canHide: true, defaultHidden: false }}
		>
			Activation platform
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			type={TD_TYPE.NONE}
			isSortable
			key={"StartDate"}
			bind={"StartDate"}
			onRender={(value, rowData) => <>{rowData?.ParentUniqueIDcode && customDateRenderer(value, rowData)}</>}
			columnSettings={{ label: "Start Date", canHide: true, defaultHidden: false }}
		>
			Start date
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			type={TD_TYPE.NONE}
			isSortable
			key={"EndDate"}
			bind={"EndDate"}
			onRender={(value, rowData) => <>{rowData?.ParentUniqueIDcode && customDateRenderer(value, rowData)}</>}
			columnSettings={{ label: "End Date", canHide: true, defaultHidden: false }}
		>
			End date
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			isSortable
			key={"Budget"}
			bind={"Budget"}
			type={TD_TYPE.NONE}
			onRender={(value, rowData) => {
				if (value && rowData?.ParentUniqueIDcode) {
					const formattedValue = isUSCurrency
						? helpers.formatCurrency(value)
						: parseFloat(value).toLocaleString(undefined, {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							});
					if (rowData?.IsPrismaBudgetLocked == "True") {
						return (
							<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
								<i className="icon-lock" />
								{formattedValue}
							</div>
						);
					}
					return <>{formattedValue}</>;
				}
				return <></>;
			}}
			columnSettings={{ label: "Budget", canHide: true, defaultHidden: false }}
		>
			Budget
		</ColumnTemplate>,
		<ColumnTemplate
			// This comment exists to prevent eslint from making this unreadable.
			type={TD_TYPE?.NONE}
			key={"Status"}
			bind={"Status"}
			onRender={(bindData, rowData, rowIndex) => {
				if (rowData?.ParentUniqueIDcode) {
					return (
						<PlatformStatusIcon
							status={enabledPlatformIds.includes(rowData?.MasterPlatformID) ? parseInt(rowData?.ExternalSystemStatusID, 10) : 5}
							tooltip={enabledPlatformIds.includes(rowData?.MasterPlatformID) ? rowData?.ExternalSystemResponse || rowData?.ExternalSystemStatusName : "Not eligible"}
							isBadge={false}
						/>
					);
				}
				return <></>;
			}}
			columnSettings={{ label: "Status", canHide: true, defaultHidden: false }}
		>
			Status
		</ColumnTemplate>
	];

	// render!
	return (
		<div>
			<ViewMasterCampaignsContext.Provider value={mvcc}>
				<DataTableContext.Provider value={{ dataProvider }}>
					<DataTable
						id={"archive-table"}
						bindKey={"CampaignID"}
						ref={dataTableApi}
						data={dataProvider}
						hasVirtualScrolling={false}
						paginationProps={{ rowOptions }}
						emptyState={
							<EmptyState
								heading={
									<Heading size={HEADING_SIZE.MEDIUM} as={HEADING_LEVEL.H2} description={"There is no data to display."}>
										No Data
									</Heading>
								}
								icon={"icon-info"}
							/>
						}
						actionBarProps={{
							search: (
								<Textbox
									size={TEXTBOX_SIZE.SMALL}
									debounceTimeout={500}
									onClear={(e) => {
										setLoading(true);
									}}
								>
									Search
								</Textbox>
							),
							filters: {
								fullFilters: [],
								quickFilters
							},
							filtersSideSheet: (
								<FiltersSidesheet
									availablePlatforms={availablePlatforms || []}
									onSave={(filters) => {
										dataProvider?.filter(filters);
									}}
									prefix={"archive"}
								/>
							),
							settingsSideSheet: true,
							showEditMode: false
						}}
						isLoading={loading}
					>
						{columns}
					</DataTable>
				</DataTableContext.Provider>
			</ViewMasterCampaignsContext.Provider>
		</div>
	);
};
